import { scoresForTask } from "@/hooks/queries/useAdlItems";

// Story 6.6 — the 34/35-per-task completeness rule (FR20), the FE advisory mirror of the
// backend EVAL-001 submit gate. Kept in one pure module so the per-task banner + the gated
// "Continue to Review" CTA (ScoringStep) and the topbar aggregate pill (ResumeEvaluationRoute)
// always agree, and so the copy matches the client-validated Screen 06 verbatim
// (docs/screens/amps-screen-06-scoring.html — countMissing / updateScoringValidation).

/** A task is valid when at most this many ADL items remain unscored (≥34 of 35 scored). */
export const MAX_UNSCORED_PER_TASK = 1;

/** Per-task completeness against the 34/35 rule. */
export interface TaskCompleteness {
  taskCode: string;
  /** 1-based tab position ("Task N"). */
  position: number;
  scoredCount: number;
  missingCount: number;
  valid: boolean;
}

export interface ScoringCompleteness {
  perTask: TaskCompleteness[];
  /** How many selected tasks have ≥2 unscored items (the "need attention" count). */
  blockedTaskCount: number;
  /** True when EVERY selected task satisfies the 34/35 rule (gates Continue). */
  allTasksValid: boolean;
}

/**
 * Compute per-task + aggregate 34/35 completeness for the selected tasks.
 *
 * `missingCount` = unscored cells in the length-`itemCount` positional array (a missing
 * key counts as fully unscored). A cell is "scored" if it holds any value (the 1–4 range
 * is the SEPARATE EVAL-004 concern); the grid only ever commits 1–4, so in practice a
 * scored cell is a valid score. Mirrors the backend `_completeness_violations` and the
 * Screen-06 `countMissing` logic exactly.
 */
export function computeScoringCompleteness(
  tasks: ReadonlyArray<{ task_code: string }>,
  scores: Record<string, (number | null)[]> | undefined,
  itemCount: number,
): ScoringCompleteness {
  const perTask: TaskCompleteness[] = tasks.map((task, index) => {
    const values = scoresForTask(scores, task.task_code, itemCount);
    const scoredCount = values.reduce<number>(
      (n, v) => (v !== null && v !== undefined ? n + 1 : n),
      0,
    );
    const missingCount = itemCount - scoredCount;
    return {
      taskCode: task.task_code,
      position: index + 1,
      scoredCount,
      missingCount,
      valid: missingCount <= MAX_UNSCORED_PER_TASK,
    };
  });
  const blockedTaskCount = perTask.filter((t) => !t.valid).length;
  return {
    perTask,
    blockedTaskCount,
    allTasksValid: blockedTaskCount === 0,
  };
}

/**
 * Topbar aggregate-pill text (Screen-06 copy).
 *
 * The verb agrees with the count: "1 task needs attention" / "2 tasks need attention".
 * This matches the mock's designed static markup (amps-screen-06-scoring.html:22 reads
 * "1 task needs attention"); only the mock's dynamic JS (line 225) drops the verb "s",
 * which would render the ungrammatical "1 task need attention" — do not copy that shortcut.
 */
export function aggregatePillText(completeness: ScoringCompleteness): string {
  if (completeness.allTasksValid) return "All tasks satisfy 34/35";
  const n = completeness.blockedTaskCount;
  return `${n} task${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} attention`;
}

/** The per-active-task advisory banner content (verbatim Screen-06 copy, AC 13). */
export interface ActiveTaskBanner {
  variant: "success" | "warn";
  /** Bolded lead — e.g. "Task 1 is valid." / "Task 1 is blocked." */
  title: string;
  body: string;
}

export function activeTaskBanner(
  position: number,
  missingCount: number,
): ActiveTaskBanner {
  const valid = missingCount <= MAX_UNSCORED_PER_TASK;
  if (valid) {
    return {
      variant: "success",
      title: `Task ${position} is valid.`,
      body: `This task has ${missingCount} missing item${
        missingCount === 1 ? "" : "s"
      }, so it satisfies the 34/35 rule.`,
    };
  }
  return {
    variant: "warn",
    title: `Task ${position} is blocked.`,
    body: `This task currently has ${missingCount} missing items. Reduce that to 1 or fewer before continuing.`,
  };
}

/** Continue-to-Review tooltip strings (verbatim Screen-06 copy, AC 14). */
export const CONTINUE_TOOLTIP_ENABLED =
  "Validation passed for every selected task. Continue to review.";
export const CONTINUE_TOOLTIP_BLOCKED =
  "Every selected task must satisfy the 34/35 rule before continuing";
